import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import { CiSearch } from 'react-icons/ci';
import { IoCopyOutline, IoPersonAddOutline, IoTrashOutline, IoWarningOutline } from 'react-icons/io5';
import Page from 'src/components/Page';
import palette from 'src/theme/palette';
import { useAuth } from 'src/context/auth/AuthContext';
import { useHeaderBreadcrumbs } from 'src/context/header/HeaderContext';
import { ALL_PERMISSIONS, getShellLabel, PERMISSION_GROUPS } from 'src/config/permissionCatalog';
import { PermissionKey, RoleShell } from 'src/types/permissions.types';
import { RoleMember } from 'src/services/role.service';
import { CreateRoleModal, ShellPicker } from 'src/components/roles/CreateRoleModal';
import { AssignRoleModal } from 'src/components/roles/AssignRoleModal';
import { DeleteRoleModal } from 'src/components/roles/DeleteRoleModal';
import { EmptyState, FilterChip, goldButtonSx, personName, roleAccent, RoleKindChip, ShellChip, StatCard } from 'src/components/roles/RoleUi';
import { useRolesAdmin } from './useRolesAdmin';

type PermissionView = 'ALL' | 'GRANTED' | 'OFF';

const outlinedButtonSx = {
  borderRadius: 1.75,
  borderColor: '#CBD5E1',
  color: '#334155',
  fontWeight: 700,
  fontSize: '0.78125rem',
  textTransform: 'none',
  py: 0.5,
  '&:hover': { bgcolor: '#F8FAFC', borderColor: '#94A3B8' },
} as const;

const greenButtonSx = {
  borderRadius: 1.75,
  bgcolor: palette.primary.main,
  color: '#FFFFFF',
  fontWeight: 700,
  fontSize: '0.78125rem',
  textTransform: 'none',
  px: 2,
  py: 0.5,
  boxShadow: `0 2px 8px ${alpha(palette.primary.main, 0.25)}`,
  '&:hover': { bgcolor: '#1E5631' },
} as const;

/** Where a role's reach ends, in words, from its view permissions. */
export function describeReach(permissions: PermissionKey[]): string {
  if (permissions.includes('*')) return 'Everything';
  if (permissions.some((p) => p.endsWith('.view_all'))) return 'Whole school';
  if (permissions.some((p) => p.endsWith('.view_assigned'))) return 'Assigned cohorts';
  if (permissions.some((p) => p.endsWith('.view_self'))) return 'Own profile';
  return 'Nothing yet';
}

const sameSet = (a: PermissionKey[], b: PermissionKey[]) => a.length === b.length && a.every((p) => b.includes(p));

export default function RoleDetail() {
  const navigate = useNavigate();
  const { roleName = '' } = useParams<{ roleName: string }>();
  const { userRole } = useAuth();
  const { roles, members, membersOf, isLoading, createRole, updateRole, deleteRole, assignRole } = useRolesAdmin();

  const role = roles.find((r) => r.name === roleName.toUpperCase());
  const roleMembers = role ? membersOf(role.name) : [];
  const isWildcard = Boolean(role?.permissions.includes('*'));
  const isOwnRole = role?.name === userRole?.toUpperCase();

  const [tab, setTab] = useState(0);
  // null means "no edits": the saved permissions show through.
  const [edits, setEdits] = useState<PermissionKey[] | null>(null);
  const [permissionSearch, setPermissionSearch] = useState('');
  const [permissionView, setPermissionView] = useState<PermissionView>('ALL');
  const [memberSearch, setMemberSearch] = useState('');
  const [settings, setSettings] = useState<{ label: string; description: string; base_route: RoleShell }>({
    label: '',
    description: '',
    base_route: '/admin',
  });
  const [isDuplicateOpen, setIsDuplicateOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState<{ member: RoleMember | null } | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  // Reset local edits whenever the stored role changes.
  useEffect(() => {
    if (!role) return;
    setEdits(null);
    setSettings({ label: role.label, description: role.description || '', base_route: role.base_route });
  }, [role?.name, role?.permissions.join(','), role?.label, role?.description, role?.base_route]);

  useHeaderBreadcrumbs(
    role
      ? {
          backUrl: '/admin/roles',
          backLabel: 'Roles',
          breadcrumbs: [{ label: 'Roles & Permissions', path: '/admin/roles' }, { label: role.label }],
        }
      : null
  );

  const stored: PermissionKey[] = role ? role.permissions.filter((p) => p !== '*') : [];
  const draft = edits ?? stored;
  const setDraft = (update: (d: PermissionKey[]) => PermissionKey[]) => setEdits(update(draft));
  const changeCount = stored.filter((p) => !draft.includes(p)).length + draft.filter((p) => !stored.includes(p)).length;
  const isDirty = !isWildcard && !sameSet(stored, draft);
  const settingsDirty =
    Boolean(role) &&
    (settings.label.trim() !== role?.label ||
      settings.description.trim() !== (role?.description || '') ||
      settings.base_route !== role?.base_route);

  const visibleGroups = useMemo(() => {
    const q = permissionSearch.trim().toLowerCase();
    return PERMISSION_GROUPS.map((group) => ({
      ...group,
      permissions: group.permissions.filter((p) => {
        if (permissionView === 'GRANTED' && !draft.includes(p.key)) return false;
        if (permissionView === 'OFF' && draft.includes(p.key)) return false;
        return !q || [p.label, p.description, p.key, group.title].some((text) => text.toLowerCase().includes(q));
      }),
    })).filter((group) => group.permissions.length > 0);
  }, [permissionSearch, permissionView, draft.join(',')]);

  const visibleMembers = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    return roleMembers.filter((m) => !q || personName(m).toLowerCase().includes(q) || m.email.toLowerCase().includes(q));
  }, [roleMembers, memberSearch]);

  if (isLoading) {
    return (
      <Box sx={{ py: 10, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <CircularProgress size={32} />
        <Typography variant="caption" sx={{ mt: 1.5, color: 'text.secondary' }}>
          Loading role workspace...
        </Typography>
      </Box>
    );
  }
  if (!role) return <Navigate to="/admin/roles" replace />;

  const accent = roleAccent(role);
  // Never let an admin remove their own access to this screen.
  const isLocked = (key: PermissionKey) => isOwnRole && key === 'roles.manage';

  const toggle = (key: PermissionKey) => {
    if (isLocked(key)) return;
    setDraft((d) => (d.includes(key) ? d.filter((p) => p !== key) : [...d, key]));
  };

  const toggleGroup = (keys: PermissionKey[], grant: boolean) => {
    setDraft((d) => {
      const editable = keys.filter((k) => !isLocked(k));
      return grant ? Array.from(new Set([...d, ...editable])) : d.filter((p) => !editable.includes(p));
    });
  };

  const savePermissions = () => updateRole.mutate({ name: role.name, patch: { permissions: draft } });
  const saveSettings = () =>
    updateRole.mutate({
      name: role.name,
      patch: {
        label: settings.label.trim() || role.label,
        description: settings.description.trim() || null,
        base_route: settings.base_route,
      },
    });

  const tabSx = {
    minHeight: 42,
    '& .MuiTab-root': { fontWeight: 700, fontSize: '0.8125rem', textTransform: 'none', minHeight: 42, px: 1.75, py: 0.5 },
  };

  return (
    <Page title={`${role.label} Role | Gold Minds Portal`}>
      <Box sx={{ width: '100%', px: { xs: 0.75, md: 1.25 }, pt: 0, pb: isDirty ? 10 : 1.5 }}>
        {/* Hero Section Header Card */}
        <Paper elevation={0} sx={{ p: { xs: 2, md: 2.25 }, mb: 1.5, borderRadius: 2.5, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1.5}>
            <Stack direction="row" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  flexShrink: 0,
                  borderRadius: 2.5,
                  bgcolor: alpha(accent.color, 0.12),
                  color: accent.color,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <Icon icon={isWildcard ? 'solar:crown-bold-duotone' : 'solar:shield-user-bold-duotone'} width={28} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                  <Typography variant="h5" fontWeight={800} color="#0F172A">
                    {role.label}
                  </Typography>
                  <RoleKindChip role={role} />
                  <ShellChip role={role} />
                  <Chip
                    label={`${roleMembers.length} ${roleMembers.length === 1 ? 'Member' : 'Members'}`}
                    size="small"
                    sx={{ bgcolor: alpha(palette.primary.main, 0.08), color: palette.primary.main, fontWeight: 700, fontSize: '0.75rem', borderRadius: 1.25, height: 24 }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 800, mb: 0.75, lineHeight: 1.4, fontSize: '0.8125rem' }}>
                  {role.description || 'No description yet. Add one in Settings so others know what this role is for.'}
                </Typography>
                <Typography variant="caption" fontWeight={700} color="text.secondary" fontSize="0.7rem">
                  KEY: <Box component="span" sx={{ fontFamily: 'monospace', color: '#334155' }}>{role.name}</Box>
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button variant="outlined" size="small" startIcon={<IoCopyOutline size={15} />} onClick={() => setIsDuplicateOpen(true)} sx={outlinedButtonSx}>
                Duplicate
              </Button>
              <Button variant="contained" size="small" startIcon={<IoPersonAddOutline size={15} />} onClick={() => setAssignTarget({ member: null })} sx={greenButtonSx}>
                Add Member
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* KPIs */}
        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
          <Grid item xs={6} sm={3}>
            <StatCard label="Members" value={roleMembers.length} caption="People with this role" icon="solar:users-group-rounded-bold-duotone" color="#059669" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              label="Permissions"
              value={isWildcard ? 'All' : `${stored.length}/${ALL_PERMISSIONS.length}`}
              caption="Actions allowed"
              icon="solar:key-minimalistic-square-bold-duotone"
              color="#7C3AED"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard label="Reach" value={describeReach(role.permissions)} caption="What they can see" icon="solar:eye-bold-duotone" color="#2563EB" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard label="Dashboard" value={getShellLabel(role.base_route).replace(' dashboard', '')} caption="Where they land" icon={accent.icon} color={accent.color} />
          </Grid>
        </Grid>

        {/* Tabbed Workspace */}
        <Paper elevation={0} sx={{ borderRadius: 2.5, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF', overflow: 'hidden' }}>
          <Box sx={{ borderBottom: 1, borderColor: '#E2E8F0', px: 2, pt: 0.5, bgcolor: '#FAFAFA' }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} textColor="primary" indicatorColor="primary" sx={tabSx}>
              <Tab label={`Permissions (${isWildcard ? 'All' : draft.length})`} />
              <Tab label={`Members (${roleMembers.length})`} />
              <Tab label="Settings" />
            </Tabs>
          </Box>

          {/* TAB 0: Permissions */}
          {tab === 0 && (
            <Box sx={{ p: { xs: 1.5, md: 2 } }}>
              {isWildcard ? (
                <Alert severity="info" icon={<Icon icon="solar:crown-bold-duotone" width={22} />} sx={{ borderRadius: 2 }}>
                  <strong>{role.label}</strong> has full access to everything, including permissions added in the future. It cannot be
                  narrowed. To give someone limited access, duplicate a narrower role instead.
                </Alert>
              ) : (
                <>
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={1.5} sx={{ mb: 1.75 }}>
                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1 }}>
                      <TextField
                        placeholder="Search permissions..."
                        size="small"
                        value={permissionSearch}
                        onChange={(e) => setPermissionSearch(e.target.value)}
                        inputProps={{ 'aria-label': 'Search permissions' }}
                        sx={{ maxWidth: 360, bgcolor: '#FFFFFF' }}
                        InputProps={{
                          sx: { height: 32, fontSize: '0.8125rem' },
                          startAdornment: (
                            <InputAdornment position="start">
                              <CiSearch size={16} color="#64748B" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" fontWeight={600} fontSize="0.75rem">
                        {draft.length} of {ALL_PERMISSIONS.length} allowed
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.75}>
                      <FilterChip label="All" active={permissionView === 'ALL'} onClick={() => setPermissionView('ALL')} />
                      <FilterChip label="Allowed" active={permissionView === 'GRANTED'} onClick={() => setPermissionView('GRANTED')} />
                      <FilterChip label="Not allowed" active={permissionView === 'OFF'} onClick={() => setPermissionView('OFF')} />
                    </Stack>
                  </Stack>

                  {visibleGroups.length === 0 ? (
                    <EmptyState icon="solar:key-minimalistic-square-bold-duotone" title="No matching permissions" message="Try a different search or filter." />
                  ) : (
                    <Grid container spacing={1.5}>
                      {visibleGroups.map((group) => {
                        const allKeys = PERMISSION_GROUPS.find((g) => g.resource === group.resource)!.permissions.map((p) => p.key);
                        const grantedCount = allKeys.filter((k) => draft.includes(k)).length;
                        const allOn = grantedCount === allKeys.length;
                        return (
                          <Grid item xs={12} lg={6} key={group.resource}>
                            <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #E2E8F0', overflow: 'hidden', height: '100%' }}>
                              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 1.75, py: 1.25, bgcolor: '#F8FAFC', borderBottom: '1px solid #F1F5F9' }}>
                                <Box sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: alpha(group.color, 0.12), color: group.color, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                                  <Icon icon={group.icon} width={18} />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="subtitle2" fontWeight={800} color="#0F172A" fontSize="0.8125rem">
                                    {group.title}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                                    {group.description}
                                  </Typography>
                                </Box>
                                <Chip
                                  label={`${grantedCount}/${allKeys.length}`}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.68rem',
                                    fontWeight: 700,
                                    bgcolor: grantedCount ? alpha(group.color, 0.12) : '#F1F5F9',
                                    color: grantedCount ? group.color : '#64748B',
                                  }}
                                />
                                <Tooltip title={allOn ? `Turn off all ${group.title}` : `Allow all ${group.title}`}>
                                  <Checkbox
                                    size="small"
                                    checked={allOn}
                                    indeterminate={grantedCount > 0 && !allOn}
                                    onChange={() => toggleGroup(allKeys, !allOn)}
                                    inputProps={{ 'aria-label': `All ${group.title}` }}
                                  />
                                </Tooltip>
                              </Stack>

                              <Box sx={{ py: 0.5 }}>
                                {group.permissions.map((p) => {
                                  const checked = draft.includes(p.key);
                                  const locked = isLocked(p.key);
                                  const changed = checked !== stored.includes(p.key);
                                  return (
                                    <Tooltip key={p.key} title={locked ? 'You cannot remove this from your own role' : ''} placement="top-start">
                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                        onClick={() => toggle(p.key)}
                                        sx={{
                                          px: 1.25,
                                          py: 0.5,
                                          cursor: locked ? 'not-allowed' : 'pointer',
                                          bgcolor: changed ? alpha('#EDB42E', 0.1) : 'transparent',
                                          '&:hover': { bgcolor: changed ? alpha('#EDB42E', 0.16) : '#F8FAFC' },
                                        }}
                                      >
                                        <Checkbox
                                          size="small"
                                          checked={checked}
                                          disabled={locked}
                                          onClick={(e) => e.stopPropagation()}
                                          onChange={() => toggle(p.key)}
                                          inputProps={{ 'aria-label': p.key }}
                                        />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                          <Typography variant="body2" fontWeight={600} color="#0F172A" fontSize="0.8125rem">
                                            {p.label}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" fontSize="0.7rem" display="block">
                                            {p.description}
                                          </Typography>
                                        </Box>
                                        {changed && (
                                          <Chip label={checked ? 'Adding' : 'Removing'} size="small" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha('#EDB42E', 0.25), color: '#854D0E' }} />
                                        )}
                                        {locked && <Icon icon="solar:lock-keyhole-bold" width={16} color="#94A3B8" />}
                                      </Stack>
                                    </Tooltip>
                                  );
                                })}
                              </Box>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </>
              )}
            </Box>
          )}

          {/* TAB 1: Members */}
          {tab === 1 && (
            <Box sx={{ p: { xs: 1.5, md: 2 } }}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={1.5} sx={{ mb: 1.75 }}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1 }}>
                  <TextField
                    placeholder="Search members by name or email..."
                    size="small"
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    inputProps={{ 'aria-label': 'Search members' }}
                    sx={{ maxWidth: 360, bgcolor: '#FFFFFF' }}
                    InputProps={{
                      sx: { height: 32, fontSize: '0.8125rem' },
                      startAdornment: (
                        <InputAdornment position="start">
                          <CiSearch size={16} color="#64748B" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" fontWeight={600} fontSize="0.75rem">
                    Showing {visibleMembers.length} of {roleMembers.length}
                  </Typography>
                </Stack>
                <Button size="small" variant="contained" startIcon={<IoPersonAddOutline size={14} />} onClick={() => setAssignTarget({ member: null })} sx={{ ...greenButtonSx, fontSize: '0.725rem' }}>
                  + Add Member
                </Button>
              </Stack>

              {visibleMembers.length === 0 ? (
                <EmptyState
                  icon="solar:users-group-rounded-bold-duotone"
                  title={memberSearch ? 'No matching members' : `Nobody has the ${role.label} role yet`}
                  message={memberSearch ? 'Try a different search.' : 'Add people to give them this role’s access.'}
                  actionLabel="Add Member"
                  onAction={memberSearch ? undefined : () => setAssignTarget({ member: null })}
                />
              ) : (
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800, color: '#334155', fontSize: '0.725rem', py: 0.75 }}>MEMBER</TableCell>
                        <TableCell sx={{ fontWeight: 800, color: '#334155', fontSize: '0.725rem', py: 0.75 }}>EMAIL ADDRESS</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: '#334155', fontSize: '0.725rem', py: 0.75 }}>
                          ACTIONS
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleMembers.map((m) => (
                        <TableRow key={m.id} hover>
                          <TableCell sx={{ py: 0.75 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Avatar sx={{ width: 28, height: 28, bgcolor: alpha(accent.color, 0.1), color: accent.color, fontSize: '0.75rem', fontWeight: 700 }}>
                                {(m.firstname || m.email)[0]?.toUpperCase()}
                              </Avatar>
                              <Typography variant="subtitle2" fontWeight={700} color="#0F172A" fontSize="0.8125rem">
                                {personName(m)}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ py: 0.75, fontSize: '0.8125rem', color: '#475569' }}>{m.email}</TableCell>
                          <TableCell align="right" sx={{ py: 0.75 }}>
                            <Button size="small" onClick={() => setAssignTarget({ member: m })} sx={{ fontWeight: 700, fontSize: '0.725rem', textTransform: 'none', color: '#334155' }}>
                              Change Role
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* TAB 2: Settings */}
          {tab === 2 && (
            <Box sx={{ p: { xs: 1.5, md: 2 } }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={800} color="#0F172A">
                  Role Configuration
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                  Rename {role.label}, explain what it is for, and choose the dashboard its members land in.
                </Typography>
              </Box>

              <Grid container spacing={2.5}>
                <Grid item xs={12} lg={7}>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #E2E8F0' }}>
                    <Stack spacing={2.5}>
                      <TextField
                        label="Role Name"
                        size="small"
                        fullWidth
                        value={settings.label}
                        onChange={(e) => setSettings((s) => ({ ...s, label: e.target.value }))}
                        helperText={`Stored key ${role.name} stays the same.`}
                      />
                      <TextField
                        label="Description"
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                        value={settings.description}
                        onChange={(e) => setSettings((s) => ({ ...s, description: e.target.value }))}
                      />
                      <Box>
                        <Typography variant="caption" fontWeight={700} color="#64748B" letterSpacing={0.5} sx={{ display: 'block', mb: 1 }}>
                          DASHBOARD
                        </Typography>
                        <ShellPicker value={settings.base_route} onChange={(shell) => setSettings((s) => ({ ...s, base_route: shell }))} disabled={role.is_system} />
                        {role.is_system && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Built-in roles keep their dashboard so existing accounts keep working.
                          </Typography>
                        )}
                      </Box>
                      <Stack direction="row" justifyContent="flex-end">
                        <Button variant="contained" disabled={!settingsDirty || updateRole.isPending} onClick={saveSettings} sx={goldButtonSx}>
                          Save Settings
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12} lg={5}>
                  {role.is_system ? (
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #E2E8F0', bgcolor: '#F8FAFC' }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Icon icon="solar:shield-check-bold-duotone" width={24} color={palette.primary.main} />
                        <Typography variant="subtitle1" fontWeight={800} color="#0F172A">
                          Protected Built-in Role
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', lineHeight: 1.45 }}>
                        {role.label} ships with Gold Minds and cannot be deleted. You can still rename it and adjust its permissions.
                      </Typography>
                    </Paper>
                  ) : (
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #FECACA', bgcolor: '#FEF2F2' }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: '#FEE2E2', color: '#DC2626', display: 'grid', placeItems: 'center' }}>
                          <IoWarningOutline size={18} />
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={800} color="#991B1B">
                            Danger Zone: Delete Role
                          </Typography>
                          <Typography variant="caption" color="#7F1D1D">
                            Permanent role removal
                          </Typography>
                        </Box>
                      </Stack>
                      <Typography variant="body2" color="#7F1D1D" sx={{ my: 1.5, fontSize: '0.8125rem', lineHeight: 1.45 }}>
                        {roleMembers.length > 0
                          ? `${roleMembers.length} ${roleMembers.length === 1 ? 'person has' : 'people have'} this role. Move them to another role before deleting it.`
                          : 'Nobody has this role, so it can be deleted safely.'}
                      </Typography>
                      <Button
                        variant="contained"
                        color="error"
                        fullWidth
                        startIcon={<IoTrashOutline size={16} />}
                        onClick={() => setIsDeleteOpen(true)}
                        sx={{ borderRadius: 2, fontWeight: 700, fontSize: '0.8125rem', py: 1, textTransform: 'none', bgcolor: '#DC2626', '&:hover': { bgcolor: '#B91C1C' } }}
                      >
                        Delete {role.label}
                      </Button>
                    </Paper>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>

        {/* Unsaved changes bar, styled like the cohort batch-actions bar */}
        {isDirty && (
          <Paper
            elevation={4}
            role="region"
            aria-label="Unsaved permission changes"
            sx={{
              position: 'fixed',
              bottom: 20,
              left: { xs: 12, lg: 'calc(240px + 24px)' },
              right: { xs: 12, md: 24 },
              zIndex: 1200,
              p: 1.25,
              pl: 2,
              borderRadius: 2,
              bgcolor: '#0F172A',
              color: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Icon icon="solar:pen-new-square-bold-duotone" width={20} color="#EDB42E" />
              <Typography variant="body2" fontWeight={700}>
                {changeCount} unsaved {changeCount === 1 ? 'change' : 'changes'} to {role.label}
              </Typography>
              {roleMembers.length > 0 && (
                <Typography variant="caption" sx={{ color: '#94A3B8', display: { xs: 'none', sm: 'inline' } }}>
                  Affects {roleMembers.length} {roleMembers.length === 1 ? 'person' : 'people'}
                </Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={() => setEdits(null)} sx={{ color: '#CBD5E1', fontWeight: 700, textTransform: 'none' }}>
                Discard
              </Button>
              <Button size="small" variant="contained" disabled={updateRole.isPending} onClick={savePermissions} sx={{ ...goldButtonSx, px: 2 }}>
                {updateRole.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </Stack>
          </Paper>
        )}

        <CreateRoleModal
          open={isDuplicateOpen}
          onClose={() => setIsDuplicateOpen(false)}
          roles={roles}
          initialSource={role.name}
          isSubmitting={createRole.isPending}
          onSubmit={async (input) => {
            const created = await createRole.mutateAsync(input);
            navigate(`/admin/roles/${created.name}`);
          }}
        />

        <AssignRoleModal
          open={Boolean(assignTarget)}
          onClose={() => setAssignTarget(null)}
          members={members}
          roles={roles}
          initialMember={assignTarget?.member}
          initialRole={assignTarget?.member ? undefined : role.name}
          isSubmitting={assignRole.isPending}
          onSubmit={(userId, name) => assignRole.mutateAsync({ userId, roleName: name })}
        />

        <DeleteRoleModal
          open={isDeleteOpen}
          role={role}
          memberCount={roleMembers.length}
          onClose={() => setIsDeleteOpen(false)}
          isDeleting={deleteRole.isPending}
          onConfirm={() =>
            deleteRole
              .mutateAsync(role)
              .then(() => navigate('/admin/roles'))
              .catch(() => undefined)
          }
        />
      </Box>
    </Page>
  );
}
